<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { useRouterStore } from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppRouterLink, AppLinkButton } from '@libs/ui';

import { ItinerarySegmentType } from '../../models';

interface AccommodationLinkContext {
  segmentType: ItinerarySegmentType.Accommodation;
  ingredientIndex: number;
}

interface ActivityLinkContext {
  segmentType: ItinerarySegmentType.Activity;
  ingredientIndex: number;
}

interface FerryLinkContext {
  segmentType: ItinerarySegmentType.Ferry;
}

interface FlightLinkContext {
  segmentType: ItinerarySegmentType.Flight;
}

type LinkContext =
  | AccommodationLinkContext
  | ActivityLinkContext
  | FlightLinkContext
  | FerryLinkContext;

interface ChangeProductLinkProps {
  linkContext: LinkContext;
}

const props = defineProps<ChangeProductLinkProps>();

interface ChangeProductLinkEmits {
  (eventName: 'navigation-triggered'): void;
}

const emit = defineEmits<ChangeProductLinkEmits>();

const { t } = i18n.global;
const routerStore = useRouterStore();

const text = computed<string>(() => {
  const segmentType = props.linkContext.segmentType;
  switch (segmentType) {
    case ItinerarySegmentType.Accommodation:
      return t('planner.alternative_hotels.change_hotel');
    case ItinerarySegmentType.Flight:
      return t('planner.change_or_see_other_flights');
    case ItinerarySegmentType.Ferry:
      return t('planner.change_or_see_other_ferries');
    case ItinerarySegmentType.Activity:
      return t('planner.activity_suggestions.explore_more_activities');
    default:
      throw new Error('Impossible case!');
  }
});

const link = ref<string>('');

function updateLink(): string {
  const segmentType = props.linkContext.segmentType;

  switch (segmentType) {
    case ItinerarySegmentType.Accommodation:
      return routerStore.getHotelSelectionUrl({
        ingredientIndex: props.linkContext.ingredientIndex,
      });
    case ItinerarySegmentType.Flight:
      return routerStore.getFlightListUrl();
    case ItinerarySegmentType.Ferry:
      return routerStore.getFerryListUrl();
    case ItinerarySegmentType.Activity:
      return routerStore.getActivitySelectionUrl({
        ingredientIndex: props.linkContext.ingredientIndex,
      });
    default:
      throw new Error('Impossible case!');
  }
}

watch(
  () => props.linkContext,
  () => {
    setTimeout(() => {
      link.value = updateLink();
    }, 1);
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <AppRouterLink
    :link="link"
    class="change-product-link"
    @navigation-triggered="emit('navigation-triggered')"
  >
    <AppLinkButton
      :text="text"
      size="m"
      bold
      right-icon="arrowRight"
      font="primary"
      underline
    />
  </AppRouterLink>
</template>

<style lang="scss" scoped>
.change-product-link {
  margin: var(--spacing-8) 0;
}
</style>
